// Call Direction
export enum CallDirection {
  INBOUND = 'INBOUND',
  OUTBOUND = 'OUTBOUND',
}

// Transfer Type
export enum TransferType {
  BLIND = 'BLIND',
  WARM = 'WARM',
  ESCALATION = 'ESCALATION',
}

// Call Status
export enum CallLogStatus {
  INITIATED = 'INITIATED',
  RINGING = 'RINGING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  BUSY = 'BUSY',
  NO_ANSWER = 'NO_ANSWER',
  CANCELED = 'CANCELED',
}

// Call Purpose
export enum CallPurpose {
  INSURANCE_VERIFICATION = 'INSURANCE_VERIFICATION',
  APPOINTMENT_SCHEDULING = 'APPOINTMENT_SCHEDULING',
  FOLLOW_UP = 'FOLLOW_UP',
  REFERRAL = 'REFERRAL',
  GENERAL_INQUIRY = 'GENERAL_INQUIRY',
  TECHNICAL_SUPPORT = 'TECHNICAL_SUPPORT',
  BILLING = 'BILLING',
}

// Agent Availability Status
export enum AgentAvailabilityStatus {
  AVAILABLE = 'AVAILABLE',
  AWAY = 'AWAY',
  DO_NOT_DISTURB = 'DO_NOT_DISTURB',
  OFFLINE = 'OFFLINE',
  ON_CALL = 'ON_CALL',
  DISABLED = 'DISABLED',
}

// Call Resolution Status
export enum CallResolutionStatus {
  RESOLVED = 'RESOLVED',
  PENDING = 'PENDING',
  TRANSFERRED = 'TRANSFERRED',
}

// Call Category
export enum CallCategory {
  TWILIO_CALL = 'TWILIO_CALL',
  BROWSER_CALL = 'BROWSER_CALL',
}

// Main interfaces

export interface CallLog {
  id: number;
  category?: CallCategory;
  twilioCallSid?: string;
  direction: CallDirection;
  status: CallLogStatus;
  fromNumber: string;
  toNumber: string;
  duration?: number;
  startTime?: string;
  endTime?: string;
  patientId?: string;
  appointmentId?: string;
  notesHistory?: ConferenceNote[];
  tags?: string[];
  rating?: number;
  resolution?: CallResolutionStatus;
  recordingUrl?: string;
  recordingSid?: string;
  recordingDuration?: number;
  transcriptUrl?: string;
  conferenceSid?: string;
  conferenceId?: number;
  conferenceName?: string;
  isTransfer?: boolean;
  transferredFrom?: number;
  transferredTo?: number;
  transferType?: TransferType;
  coreProviderId?: string;
  coreProviderFullname?: string;
  coreMemberId?: number;
  coreMemberFullname?: string;
  createdAt: string;
  updatedAt: string;
  caller?: {
    name: string;
    id: number | null;
    phoneNumber: string;
    isAgent: boolean;
    isFromBrowser: boolean;
  };
  callee?: {
    name: string;
    id: number | null;
    phoneNumber: string;
    isAgent: boolean;
    isToBrowser: boolean;
  };
  from?: {
    name: string;
    id?: number;
    phoneNumber: string;
    location?: string;
  };
  to?: {
    name: string;
    id?: number;
    phoneNumber: string;
    location?: string;
  };
  purpose?: CallPurpose;
  agentName?: string;
  isMainLineInvolved?: boolean;
}

export interface AgentAvailability {
  id: number;
  coreProviderId: string;
  status: AgentAvailabilityStatus;
  phoneNumber?: string;
  currentConferenceId?: string;
  lastStatusChange: string;
}

export interface DashboardData {
  agentStatus?: {
    currentStatus: AgentAvailabilityStatus;
    lastStatusChange: string;
  };
  activeCalls?: {
    calls: CallLog[];
    totalActive: number;
  };
  queueMetrics?: {
    waitingCalls: number;
    averageWaitTime: number;
    totalCallsToday: number;
    completedCallsToday: number;
  };
}

// Request/Response interfaces
export interface InitiateCallRequest {
  toNumber: string;
  patientId?: string;
  callPurpose?: string;
}

export interface InitiateCallResponse {
  success: boolean;
  call?: CallLog;
  message?: string;
}

export interface EndCallRequest {
  resolution?: CallResolutionStatus;
  rating?: number;
}

export interface UpdateAgentAvailabilityRequest {
  status: AgentAvailabilityStatus;
  coreProviderId?: number;
  phoneNumber?: string;
  currentConferenceId?: number;
}

export interface TransferCallRequest {
  type: TransferType;
  targetNumber?: string;
  targetCoreProviderId?: number;
  context?: string;
}

export interface InitiateTransferResponse {
  transferCallLogId: string;
  status: string;
  targetAgentName: string;
  participantCallSid: string;
}

export interface TransferActionResponse {
  success: boolean;
}

export interface AddNoteRequest {
  content: string;
}

export interface RateCallRequest {
  rating: number;
  feedback?: string;
}

export interface CallHistoryQuery {
  coreProviderId?: number;
  patientId?: string;
  statuses?: CallLogStatus[];
  direction?: CallDirection;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
  callId?: number;
  callSid?: string;
  // Clinic filter (server clamps to the caller's permitted clinics).
  clinicIds?: number[];
  includeUnassigned?: boolean;
}

export interface CallRecordingResponse {
  url: string;
  duration?: number;
}

export interface FindAllCallsParams {
  searchString?: string;
  fromNumber?: string;
  toNumber?: string;
  direction?: CallDirection;
  status?: CallLogStatus[];
  purpose?: CallPurpose[];
  patientId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
  // Legacy support
  from?: string;
  to?: string;
  coreMemberId?: number;
  coreProviderId?: number;
  // Clinic filter (server clamps to the caller's permitted clinics).
  clinicIds?: number[];
  includeUnassigned?: boolean;
}

export interface ConferenceNote {
  id: number;
  conferenceId: number;
  content: string;
  coreProviderId?: number;
  createdAt: string;
  updatedAt: string;
}

export enum ParticipantType {
  Agent = 'AGENT',
  External = 'EXTERNAL',
}

export interface ConferenceParticipant {
  id: number;
  conferenceId: number;
  twilioCallSid: string;
  participantType: ParticipantType;
  coreProviderId?: number;
  coreMemberId?: number;
  phoneNumber?: string;
  status: string;
  muted: boolean;
  hold: boolean;
  holdStartTime?: string;
  joinTime?: string;
  leaveTime?: string;
  callLog?: CallLog;
}

export interface Conference {
  conferenceId: number;
  id?: number;
  twilioConferenceSid?: string;
  friendlyName?: string;
  status?: string;
  createdAt: string;
  duration: number | null;
  recordingUrl: string | null;
  recordingDuration: number | null;
  notes: ConferenceNote[];
  calls: CallLog[];
  participants?: ConferenceParticipant[];
}

export interface DirectoryTrackingNumber {
  id: number;
  number: string;
  label: string | null;
  isPrimary: boolean;
  priority: number;
}

export interface RawAgentDirectoryEntry {
  id: number;
  name: string;
  coreProviderId: number;
  trackingNumbers: {
    incoming: DirectoryTrackingNumber[];
    outgoing: DirectoryTrackingNumber[];
    sms: DirectoryTrackingNumber[];
  };
  status: AgentAvailabilityStatus;
  currentConferenceId?: string;
  queues: { id: number; name: string }[];
  agentTeams?: { team: { id: number; name: string } }[];
}

export interface AgentDirectoryEntry extends RawAgentDirectoryEntry {
  phoneNumber?: string;
}

export interface SearchAgentsParams {
  coreProviderId?: number;
  status?: AgentAvailabilityStatus;
  page?: number;
  pageSize?: number;
}

// Legacy interfaces for compatibility
export interface SimplifiedMember {
  id: number;
  phone: string;
  name: string;
}

export interface SimplifiedProvider {
  id: number;
  phone: string;
  name: string;
}

export enum TrackingNumberStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export enum AgentTrackingNumberUsageCategory {
  INCOMING = 'INCOMING',
  OUTGOING = 'OUTGOING',
  SMS = 'SMS',
}

export interface AgentAssignment {
  id: number;
  agentId: number;
  coreProviderId: number | null;
  usageCategory: AgentTrackingNumberUsageCategory;
  isPrimary: boolean;
  priority: number;
  agentStatus?: AgentAvailabilityStatus;
  agentName?: string | null;
}

export interface TrackingNumberListItem {
  id: number;
  trackingNumber: string;
  status: TrackingNumberStatus;
  label: string | null;
  isMainLine: boolean;
  isToolFree?: boolean;
  enabledForVoiceCalls: boolean;
  enabledForReminders: boolean;
  totalAgentsAssigned: number;
  callRouteName?: string | null;
}

export interface TrackingNumberDetail {
  id: number;
  trackingNumber: string;
  status: TrackingNumberStatus;
  label: string | null;
  isMainLine: boolean;
  isTollFreeLine: boolean;

  // Voice features
  enabledForVoiceCalls: boolean;
  enabledForInboundCalls: boolean;
  enabledForOutboundCalls: boolean;
  enabledForVoicemail: boolean;
  maxConcurrentCalls: number | null;

  // Other features
  enabledForReminders: boolean;

  // Call route
  callRouteId: number | null;
  callRouteName?: string | null;

  // Agent assignments
  agentAssignments: AgentAssignment[];

  // Statistics
  totalAgentsAssigned: number;
  incomingAgentsCount: number;
  outgoingAgentsCount: number;
  smsAgentsCount: number;

  createdAt: string;
  updatedAt: string;
}

export interface ListTrackingNumbersParams {
  status?: TrackingNumberStatus;
  usageCategory?: AgentTrackingNumberUsageCategory;
  enabledForVoiceCalls?: boolean;
  enabledForReminders?: boolean;
  search?: string;
}

export interface UpdateTrackingNumberRequest {
  status?: TrackingNumberStatus;
  label?: string;
  enabledForVoiceCalls?: boolean;
  enabledForInboundCalls?: boolean;
  enabledForOutboundCalls?: boolean;
  enabledForVoicemail?: boolean;
  enabledForReminders?: boolean;
  maxConcurrentCalls?: number;
  callRouteId?: number;
}

export interface AssignAgentRequest {
  agentId: number;
  usageCategory: AgentTrackingNumberUsageCategory;
  isPrimary?: boolean;
  priority?: number;
}

export interface UnassignAgentRequest {
  agentId: number;
  usageCategory: AgentTrackingNumberUsageCategory;
}

// ===== NEW: Call Metrics Interfaces =====

export interface CallMetricsInboundDetails {
  answered: number;
  abandoned: number;
  missed: number;
  rejected: number;
  answerRate: number;
  abandonRate: number;
}

export interface CallMetricsOutboundDetails {
  completed: number;
  failed: number;
  noAnswer: number;
  successRate: number;
}

export interface RatingDistribution {
  '1': number;
  '2': number;
  '3': number;
  '4': number;
  '5': number;
}

export interface AgentRatingMetrics {
  agentId: number;
  agentName: string;
  totalCalls: number;
  evaluatedCalls: number;
  unevaluatedCalls: number;
  evaluationRate: number;
  averageRating: number;
  positiveRatings: number;
  neutralRatings: number;
  negativeRatings: number;
  positiveRatingRate: number;
  ratingDistribution: RatingDistribution;
}

export interface TeamRatingMetrics {
  teamId: number;
  teamName: string;
  totalCalls: number;
  evaluatedCalls: number;
  unevaluatedCalls: number;
  evaluationRate: number;
  averageRating: number;
  positiveRatings: number;
  neutralRatings: number;
  negativeRatings: number;
  positiveRatingRate: number;
  ratingDistribution: RatingDistribution;
}

export interface CallRatingMetrics {
  totalCalls: number;
  evaluatedCalls: number;
  unevaluatedCalls: number;
  evaluationRate: number;
  ratingDistribution: RatingDistribution;
  averageRating: number;
  medianRating: number;
  positiveRatings: number;
  neutralRatings: number;
  negativeRatings: number;
  positiveRatingRate: number;
  ratingsByAgent?: AgentRatingMetrics[];
  ratingsByTeam?: TeamRatingMetrics[];
}

export interface CallPurposeMetrics {
  purpose: string;
  totalCalls: number;
  inboundCalls: number;
  outboundCalls: number;
  completedCalls: number;
  averageDuration: number;
  evaluatedCalls: number;
  averageRating: number;
  positiveRatingRate: number;
  completionRate: number;
}

export interface TopPurpose {
  purpose: string;
  count: number;
  percentage: number;
}

export interface CallMetricsByStatus {
  status: string;
  count: number;
}

export interface CallMetricsMissedByAgent {
  agentId: number;
  agentName: string;
  count: number;
  totalCalls: number;
}

export interface CallMetricsByAgent {
  agentId: number;
  agentName: string;
  teams?: string; // Comma-separated team names
  count: number;
  inboundCount: number;
  outboundCount: number;
  answeredCalls: number;
  missedCalls: number;
  averageDuration: number;
  averageWaitTime: number;
  averageAnswerTime: number;
  inboundDetails?: CallMetricsInboundDetails;
  outboundDetails?: CallMetricsOutboundDetails;
  ratingMetrics?: AgentRatingMetrics;
  transfersInitiated?: number;
  transfersReceived?: number;
}

export interface CallMetricsByTeam {
  teamId: number;
  teamName: string;
  totalCalls: number;
  inboundCalls: number;
  outboundCalls: number;
  answeredCalls: number;
  abandonedCalls: number;
  averageDuration: number;
  averageWaitTime: number;
  averageAnswerTime: number;
  inboundDetails?: CallMetricsInboundDetails;
  outboundDetails?: CallMetricsOutboundDetails;
  ratingMetrics?: TeamRatingMetrics;
}

export interface CallMetricsPeakHours {
  hour: number;
  count: number;
}

export interface TransferMetricsByAgent {
  agentId: number;
  agentName: string;
  transfersInitiated: number;
  blindTransfersInitiated: number;
  warmTransfersInitiated: number;
  transfersReceived: number;
  blindTransfersReceived: number;
  warmTransfersReceived: number;
}

export interface TransferMetrics {
  totalTransfers: number;
  blindTransfers: number;
  warmTransfers: number;
  completedTransfers: number;
  failedTransfers: number;
  transfersByAgent: TransferMetricsByAgent[];
}

// NEW: IVR Route metrics
export interface CallMetricsByIvrRoute {
  routeId: number;
  routeName: string;
  totalCalls: number;
  completedCalls: number;
  completionRate: number;
  averageDuration: number;
  callsByOption: {
    option: string; // "1", "2", etc., "direct" for no option, or "after_hours" for after-hours voicemail
    count: number;
    completionRate: number;
    averageDuration: number;
  }[];
}

export interface CallMetrics {
  totalCalls: number;
  inboundCalls: number;
  outboundCalls: number;
  answeredCalls: number;
  abandonedCalls: number;
  missedCallsByAgent: CallMetricsMissedByAgent[];
  averageDuration: number;
  totalDuration: number;
  averageWaitTime: number;
  averageAnswerTime: number;
  callsByStatus: CallMetricsByStatus[];
  callsByAgent: CallMetricsByAgent[];
  callsByTeam?: CallMetricsByTeam[];
  peakHours: CallMetricsPeakHours[];
  callsWithRecordings: number;
  transferredCalls: number;
  // NEW: Detailed metrics
  inboundDetails?: CallMetricsInboundDetails;
  outboundDetails?: CallMetricsOutboundDetails;
  ratingMetrics?: CallRatingMetrics;
  callsByPurpose?: CallPurposeMetrics[];
  topPurposes?: TopPurpose[];
  callsWithoutPurpose?: number;
  // NEW: IVR metrics
  directToAgentCalls?: number;
  ivrRoutedCalls?: number;
  afterHoursCalls?: number; // Calls redirected to voicemail outside business hours
  callsByIvrRoute?: CallMetricsByIvrRoute[];
  transferMetrics?: TransferMetrics;
}

export interface CallMetricsResponse {
  current: CallMetrics;
  comparison: CallMetrics | null;
}

// --- NEW INTERFACES FOR ADMIN SECTIONS ---

// Agents
export interface AgentTrackingNumberAssignment {
  trackingNumberId: number;
  usageCategory: AgentTrackingNumberUsageCategory;
  isPrimary?: boolean;
  priority?: number;
}

export type AgentTeamRole = 'MEMBER' | 'LEADER';

export interface TeamAssignment {
  teamId: number;
  teamRole: AgentTeamRole;
  team?: Team; // For display purposes when loading from API
}

export interface CreateAgentRequest {
  coreProviderId: number;
  name?: string;
  roles: string[];
  teamAssignments?: TeamAssignment[];
}

export interface UpdateAgentRequest {
  name?: string;
  status?: AgentAvailabilityStatus;
  roles?: string[];
  teamIds?: number[]; // Legacy, prefer teamAssignments
  teamAssignments?: TeamAssignment[];
  trackingNumbers?: AgentTrackingNumberAssignment[];
  queueIds?: number[];
}

export interface AgentTeamDetail {
  team: Team;
  teamRole: AgentTeamRole;
}

export interface AgentDetail extends AgentAvailability {
  id: number; // Internal ID
  name: string | null;
  roles: string[];
  agentTeams: AgentTeamDetail[];
  agentTrackingNumbers: { trackingNumber: TrackingNumberDetail }[];
  queues?: CallQueue[];
}

// Teams
export interface Team {
  id: number;
  name: string;
  _count?: {
    AgentTeam: number;
  };
}

export interface CreateTeamRequest {
  name: string;
}

export interface UpdateTeamRequest extends CreateTeamRequest {}

export interface TeamDetail extends Team {
  AgentTeam: {
    agent: AgentDetail;
  }[];
}

// Queues & Routes
export interface CallQueue {
  id?: number;
  displayName: string;
  description?: string;
  maxWaitTime?: number;
  allowVoicemail?: boolean;
  voicemailPrompt?: string;
  ivrDigit?: string;
  priority?: number;
  isActive?: boolean;
  callRouteId?: number;
}

export interface CallRoute {
  id: number;
  displayName: string;
  description?: string;
  ivrPrompt?: string;
  audioUrl?: string;
  businessHoursEnabled?: boolean;
  businessHoursStart?: string;
  businessHoursEnd?: string;
  businessHoursTimezone?: string;
  businessHoursDays?: number[];
  afterHoursAudioUrl?: string;
  afterHoursTextPrompt?: string;
  holidayAudioUrl?: string;
  holidayTextPrompt?: string;
  callQueues?: CallQueue[];
}

export interface CreateCallRouteRequest {
  displayName: string;
  description?: string;
  ivrPrompt?: string;
  audioUrl?: string;
  businessHoursEnabled?: boolean;
  businessHoursStart?: string;
  businessHoursEnd?: string;
  businessHoursTimezone?: string;
  businessHoursDays?: number[];
  afterHoursAudioUrl?: string;
  afterHoursTextPrompt?: string;
  holidayAudioUrl?: string;
  holidayTextPrompt?: string;
  queues?: CallQueue[];
}

export interface UpdateCallRouteRequest extends CreateCallRouteRequest {}

// Tracking Numbers (Create)
export interface CreateTrackingNumberRequest extends UpdateTrackingNumberRequest {
  trackingNumber: string;
  identifier: string;
  isMainLine?: boolean;
  isTollFreeLine?: boolean;
}
